import React from 'react';
import classNames from 'classnames';
import { motion } from 'framer-motion';

import LogoImage from '../../assets/cc-logo-square-cc.png';

import { ChangingTip } from '../../components/custom';

interface Props {
  isFullPage?: boolean;
  noTips?: boolean;
}

const PageSpinner: React.FC<Props> = (props) => {
  const { isFullPage, noTips } = props;

  const logoVariants = {
    initial: {
      scale: 1,
      opacity: 0.48,
    },
    animate: {
      scale: [1, 0.9, 0.9, 1, 1],
      opacity: [1, 0.48, 0.48, 1, 1],
    },
  };

  const innerBorderVariants = {
    initial: {
      scale: 1.2,
      rotate: 270,
      opacity: 0.25,
      borderRadius: '25%',
    },
    animate: {
      scale: [1.2, 1, 1, 1.2, 1.2],
      rotate: [270, 0, 0, 270, 270],
      opacity: [0.25, 1, 1, 1, 0.25],
      borderRadius: ['25%', '25%', '50%', '50%', '25%'],
    },
  };

  const outerBorderVariants = {
    initial: {
      scale: 1.2,
      rotate: 270,
      opacity: 0.35,
      borderRadius: '25%',
    },
    animate: {
      scale: [1, 1.2, 1.2, 1, 1],
      rotate: [0, 270, 270, 0, 0],
      opacity: [1, 0.25, 0.25, 0.25, 1],
      borderRadius: ['25%', '25%', '50%', '50%', '25%'],
    },
  };

  const tipsList = [
    'Until upload completes, refrain from clicking the back button or refreshing the page',
    'Until upload completes, ensure you remain connected to the internet',
    "Until upload completes, don't log out or exit your browser",
    'To create a compelling video, use a variety of shots: drone, wide, close up etc',
    'To ensure facial recognition success, use good lighting',
    'You may upload up to <b>3</b> photos for each person',
    'Videographer sales skyrocket by <b>28%</b> using CrowdClip®',
    'CrowdClip® turns one video asset into hundreds, effortlessly',
    'Videographers save an average of <b>1,673</b> hours using CrowdClip®',
    '<b>43,114</b> minutes of footage analysed and counting!',
  ];

  return (
    <div
      className={classNames(
        'fixed top-0 left-0 lg:relative flex items-center justify-center px-8 lg:px-0',
        { 'w-screen h-screen': isFullPage, 'w-full h-full': !isFullPage },
      )}
    >
      <div className="relative w-full flex items-center justify-center">
        <motion.div
          variants={logoVariants}
          initial="initial"
          animate="animate"
          transition={{
            duration: 2,
            ease: 'easeInOut',
            repeatDelay: 1,
            repeat: Infinity,
          }}
        >
          <motion.div
            style={{ width: 64, height: 64 }}
            initial={{ scale: 1 }}
            animate={{ scale: 1.1 }}
            transition={{
              yoyo: Infinity,
              duration: 2,
              repeatType: 'reverse',
            }}
          >
            <img
              src={LogoImage}
              alt="Company logo"
              className="rounded-full"
              style={{ width: '100%', height: '100%' }}
            />
          </motion.div>
        </motion.div>

        <motion.div
          variants={innerBorderVariants}
          className="absolute w-[90px] h-[90px] rounded-[25%] border-3 border-[rgba(240,90,91,0.25)] border-solid"
          initial="initial"
          animate="animate"
          transition={{
            ease: 'linear',
            duration: 3,
            repeat: Infinity,
          }}
        />

        <motion.div
          variants={outerBorderVariants}
          className="absolute w-[110px] h-[110px] rounded-[25%] border-[6px] border-[rgba(220,69,107,0.35)] border-solid"
          initial="initial"
          animate="animate"
          transition={{
            ease: 'linear',
            duration: 3,
            repeat: Infinity,
          }}
        />

        {!noTips && (
          <ChangingTip
            tipsList={tipsList}
            className="absolute bottom-[-150%] sm:bottom-[-110%] text-center"
          />
        )}
      </div>
    </div>
  );
};

export default PageSpinner;
